import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import asyncService from '../../../../datahub/asyncService';
import selectUser from '../../../store/selectors/appSelector';
import { BLACK } from '../../../theme/colorConstants';
import { getId } from '../helpers';

import { textFieldSx } from './AutoSaveTextField';

const SEARCH_DEBOUNCE_MS = 400;

const searchIconSx = { color: BLACK[60], fontSize: '18px' };

const spinnerSx = { color: BLACK[60] };

const optionSecondaryStyle = { color: BLACK[60], fontSize: '12px' };

const AsyncEntitySearch = ({
  testId,
  placeholder,
  route,
  buildRequestData,
  optionLabel,
  renderOptionSecondary,
  excludeIds = [],
  onSelect,
  disabled = false,
  autoFocus = false,
}) => {
  const dispatch = useDispatch();
  const { token, refreshToken } = useSelector(selectUser) || {};
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let isCancelled = false;
    const timer = setTimeout(() => {
      setIsLoading(true);
      asyncService({
        httpMethod: 'POST',
        route,
        data: buildRequestData(searchTerm),
        token,
        refreshToken,
        dispatch,
        onSuccess: (response) => {
          if (isCancelled) return;
          setOptions(response?.data?.documents || []);
          setIsLoading(false);
        },
        onError: () => {
          if (isCancelled) return;
          setOptions([]);
          setIsLoading(false);
        },
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
      setIsLoading(false);
    };
  }, [buildRequestData, dispatch, isOpen, refreshToken, route, searchTerm, token]);

  const excluded = new Set(excludeIds);
  const visibleOptions = options.filter((option) => !excluded.has(getId(option)));

  return (
    <Autocomplete
      data-testid={testId}
      disabled={disabled}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      options={visibleOptions}
      loading={isLoading}
      value={null}
      inputValue={searchTerm}
      onInputChange={(event, newValue, reason) => {
        if (reason !== 'reset') setSearchTerm(newValue);
      }}
      onChange={(event, option) => {
        if (!option) return;
        setSearchTerm('');
        onSelect(option);
      }}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option?.[optionLabel] || ''}
      isOptionEqualToValue={(option, value) => getId(option) === getId(value)}
      renderOption={(props, option) => (
        <li {...props} key={getId(option)}>
          <div>
            <div>{option[optionLabel]}</div>
            {renderOptionSecondary && (
              <div style={optionSecondaryStyle}>{renderOptionSecondary(option)}</div>
            )}
          </div>
        </li>
      )}
      popupIcon={null}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          placeholder={placeholder}
          size="small"
          sx={textFieldSx}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={searchIconSx} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isLoading && <CircularProgress size={16} sx={spinnerSx} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

AsyncEntitySearch.propTypes = {
  testId: PropTypes.string,
  placeholder: PropTypes.string,
  route: PropTypes.string.isRequired,
  buildRequestData: PropTypes.func.isRequired,
  optionLabel: PropTypes.string.isRequired,
  renderOptionSecondary: PropTypes.func,
  excludeIds: PropTypes.arrayOf(PropTypes.string),
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

export default AsyncEntitySearch;
